import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { Button } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import { notification } from '@spark/ui-components/utils/notification';
import {
  ASSOCIACAO_PREMIACAO_MAX_RECORDS,
  ASSOCIACAO_PREMIACAO_PAGE_SIZE,
  deletePremiacaoTabelaVeiculo,
  getPremiacaoTabelaVeiculos
} from '@spark/services/premiacao';
import { fetchPremiacaoPaginatedList } from '../utils/fetchPremiacaoPaginatedList';
import { getVeiculoTabelaVinculoColumns } from '../config/veiculoTabelaVinculoColumns';

const pick = (data, ...keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (data?.[key] !== undefined && data?.[key] !== null) {
      return data[key];
    }
  }
  return undefined;
};

const CampoResumo = ({ label, value }) => (
  <Col md={6} className="mb-2">
    <div className="text-muted small">{label}</div>
    <div>{value || '-'}</div>
  </Col>
);

const PremiacaoVeiculoGerenciarModal = ({
  show,
  onHide,
  veiculo,
  onSuccess
}) => {
  const [vinculos, setVinculos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);

  const carregarVinculos = useCallback(async (numeroFrota) => {
    if (!numeroFrota) {
      setVinculos([]);
      return;
    }

    try {
      setLoading(true);
      const result = await fetchPremiacaoPaginatedList({
        fetchPage: getPremiacaoTabelaVeiculos,
        baseFilters: {
          NumeroFrota: numeroFrota,
          Ativo: true
        },
        pageSize: ASSOCIACAO_PREMIACAO_PAGE_SIZE,
        maxRecords: ASSOCIACAO_PREMIACAO_MAX_RECORDS
      });

      setVinculos(result.registros.map((item) => ({
        id: pick(item, 'id', 'Id'),
        idTabelaPremiacao: pick(item, 'idTabelaPremiacao', 'IdTabelaPremiacao'),
        nomeTabelaPremiacao: pick(item, 'nomeTabelaPremiacao', 'NomeTabelaPremiacao') || '',
        data: pick(item, 'data', 'Data'),
        usuarioCriacao: pick(item, 'usuarioCriacao', 'UsuarioCriacao') || ''
      })));
    } catch (error) {
      console.error('Erro ao carregar vinculos da frota:', error);
      notification.error('Erro ao carregar vinculos da frota');
      setVinculos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show) {
      setVinculos([]);
      setLoadingDeleteId(null);
      return;
    }
    carregarVinculos(veiculo?.numeroFrota);
  }, [show, veiculo?.numeroFrota, carregarVinculos]);

  const handleDeleteVinculo = useCallback(async (id) => {
    if (!id || loadingDeleteId === id) {
      return;
    }

    try {
      setLoadingDeleteId(id);
      await deletePremiacaoTabelaVeiculo(id);
      notification.success('Vinculo removido com sucesso!');
      await carregarVinculos(veiculo?.numeroFrota);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      notification.error('Erro ao remover vinculo');
      console.error('Erro ao remover vinculo:', error);
    } finally {
      setLoadingDeleteId(null);
    }
  }, [carregarVinculos, loadingDeleteId, onSuccess, veiculo?.numeroFrota]);

  const columns = useMemo(
    () => getVeiculoTabelaVinculoColumns(handleDeleteVinculo, loadingDeleteId),
    [handleDeleteVinculo, loadingDeleteId]
  );

  return (
    <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
      <Modal.Header closeButton>
        <Modal.Title>Gerenciar Frota</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {veiculo ? (
          <>
            <Row className="mb-3">
              <CampoResumo label="Numero Frota" value={veiculo.numeroFrota} />
              <CampoResumo label="Placa" value={veiculo.placa} />
              <CampoResumo label="Segmento" value={veiculo.segmento} />
              <CampoResumo label="Modelo" value={veiculo.modelo} />
            </Row>

            <h6>Tabelas de Premiacao Vinculadas</h6>
            <TanStackTable
              columns={columns}
              data={vinculos}
              loading={loading}
              withPagination={false}
              enableRowColors={true}
              enableCopy={true}
              className="mt-2"
            />
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PremiacaoVeiculoGerenciarModal;
